import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, getPage, post } from "../../lib/api";
import { useSession } from "../../lib/session";
import type { GoverningAllowance, Policy } from "../../lib/types";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Feedback";
import { SelectField } from "../ui/Input";
import { MicroLabel } from "../ui/Text";
import { HOLDERS_KEY } from "./HolderSelect";

/**
 * Assigning a policy to a holder, or clearing its own.
 *
 * Assignment is a two-step: pick, then press. Deliberately not a select whose
 * `onChange` assigns — an accidental keyboard arrow on a focused dropdown would
 * silently rewrite a whole pool's quota and close the previous assignment.
 *
 * Clearing is offered only for the holder's *own* policy. An inherited one is
 * cleared on its owner, whose page the panel links to; clearing it from here
 * would change every other member of that pool too.
 */
export function AssignAllowance({
  holderId,
  allowance,
}: {
  readonly holderId: string;
  readonly allowance: GoverningAllowance | null;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState("");
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [notice, setNotice] = useState<
    { readonly tone: "ok" | "error"; readonly text: string } | undefined
  >(undefined);

  const canAssign = can("identities.assign_allowance");
  const canClear = can("identities.remove_allowance");
  const ownPolicyId = allowance !== null && allowance.holder.id === holderId ? allowance.policy.id : undefined;

  const policies = useQuery({
    queryKey: ["allowance-policies"],
    queryFn: () => getPage<Policy>("/allowance-policies"),
    staleTime: 5 * 60_000,
    enabled: canAssign,
  });

  function settled(text: string) {
    setNotice({ tone: "ok", text });
    setSelected("");
    // The pool's figure, every member's inherited policy, and the policy counts.
    // Every cached holder page: a policy on a group governs all its descendants.
    void queryClient.invalidateQueries({ queryKey: ["holder"] });
    void queryClient.invalidateQueries({ queryKey: HOLDERS_KEY });
    void queryClient.invalidateQueries({ queryKey: ["identities"] });
    void queryClient.invalidateQueries({ queryKey: ["identity"] });
    void queryClient.invalidateQueries({ queryKey: ["allowance-policies"] });
  }

  function failed(fallback: string) {
    return (error: unknown) =>
      setNotice({ tone: "error", text: error instanceof Error ? error.message : fallback });
  }

  const assign = useMutation({
    mutationFn: (policyId: string) =>
      post(`/holders/${encodeURIComponent(holderId)}/allowance`, { policyId }),
    onSuccess: () => settled("Policy assigned. The previous assignment was closed."),
    onError: failed("The assignment failed."),
  });

  const clear = useMutation({
    mutationFn: () => del(`/holders/${encodeURIComponent(holderId)}/allowance`),
    onSuccess: () =>
      settled("Policy removed. Members now draw on the nearest group above with one, if any."),
    onError: failed("Removing the policy failed."),
  });

  if (!canAssign && !canClear) {
    return <MicroLabel>Setting an allowance needs the Assign allowances permission</MicroLabel>;
  }

  return (
    <div className="flex flex-col gap-4">
      {notice === undefined ? null : <Notice tone={notice.tone}>{notice.text}</Notice>}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (selected === "") return;
          setNotice(undefined);
          assign.mutate(selected);
        }}
        className="grid grid-cols-1 items-end gap-x-6 gap-y-4 border-t border-rule pt-4 md:grid-cols-[2fr_auto_auto]"
      >
        <SelectField
          label={ownPolicyId === undefined ? "Assign a policy" : "Change policy"}
          value={selected}
          disabled={!canAssign}
          onChange={(event) => setSelected(event.target.value)}
        >
          <option value="">{canAssign ? "Select a policy…" : "Needs Assign allowances"}</option>
          {(policies.data?.rows ?? []).map((policy) => (
            <option key={policy.id} value={policy.id} disabled={policy.id === ownPolicyId}>
              {policy.name} · {policy.quotaKwh} kWh · {policy.periodType.replace(/_/g, " ")}
              {policy.id === ownPolicyId ? " (current)" : ""}
            </option>
          ))}
        </SelectField>

        <Button
          type="submit"
          variant="primary"
          disabled={!canAssign || selected === "" || selected === ownPolicyId}
          pending={assign.isPending}
        >
          {ownPolicyId === undefined ? "Assign" : "Reassign"}
        </Button>

        {ownPolicyId === undefined || !canClear ? (
          <span />
        ) : confirmingClear ? (
          <div className="flex items-center gap-2">
            <Button
              variant="danger"
              pending={clear.isPending}
              onClick={() => {
                setConfirmingClear(false);
                setNotice(undefined);
                clear.mutate();
              }}
            >
              Confirm removal
            </Button>
            <Button variant="ghost" onClick={() => setConfirmingClear(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="danger" onClick={() => setConfirmingClear(true)}>
            Remove policy
          </Button>
        )}
      </form>
    </div>
  );
}
